/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useCallback, useMemo } from "react";
import { Button, Box, ModalConfirm, theme } from "@nordcloud/gnui";
import { usePeopleQuery, Person } from "~/generated/graphql";
import {
  BreadcrumbsBox,
  ReactTable,
  Pagination,
  ErrorWrap,
  SearchTable,
  LoaderWrap,
  GlobalSpinner,
  TableLoader,
} from "~/components";
import { useQueryState, useDisclosure } from "~/hooks";
import {
  useAddPerson,
  useRemovePerson,
  useUpdatePerson,
} from "~/hooks/graphql";
import { generateActionConfirmText, isNotNil } from "~/tools";
import { EmployeeForm } from "./EmployeeForm";
import { EmployeesListColumns } from "./EmployeesListColumns";
import { prepareRows } from "./utils";

export function EmployeesPage() {
  const {
    isOpen: isSidebarOpen,
    close: closeSidebar,
    open: openSidebar,
  } = useDisclosure();
  const {
    isOpen: isModalOpen,
    close: closeModal,
    open: openModal,
  } = useDisclosure();

  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteData, setDeleteData] = useState({
    id: "",
    name: "",
  });
  const [initialValues, setInitialValues] = useState({
    id: "",
    name: "",
    email: "",
  });

  const { state: queryState } = useQueryState();

  const { data, loading, error } = usePeopleQuery();
  const [addPerson, { loading: isAddPersonLoading }] = useAddPerson();
  const [removePerson, { loading: isRemovePersonLoading }] = useRemovePerson();
  const [updatePerson, { loading: isUpdatePersonLoading }] = useUpdatePerson();

  const handleAddPerson = async (values: { name: string; email: string }) => {
    await addPerson(values, () => {
      closeSidebar();
    });
  };

  const handleUpdatePerson = async (values: {
    id: string;
    name: string;
    email: string;
  }) => {
    await updatePerson(values, () => {
      setIsEditMode(false);
      closeSidebar();
    });
  };

  const handleRemovePerson = useCallback(
    async (id: string) => {
      closeModal();
      await removePerson({ id, name: deleteData.name });
    },
    [removePerson, deleteData, closeModal]
  );

  const onEditHandler = useCallback(
    (item: Person) => {
      setInitialValues({
        id: item.id,
        name: item.name,
        email: item.email,
      });
      setIsEditMode(true);
      openSidebar();
    },
    [setIsEditMode, openSidebar]
  );

  const columns = useMemo(
    () =>
      EmployeesListColumns({
        onEditHandler,
        setDeleteData,
        openModal,
      }),
    [onEditHandler, setDeleteData, openModal]
  );

  const people = (data?.people ?? []).filter(isNotNil);
  const filteredPeople = prepareRows(people, queryState);

  const isMutationLoading =
    isAddPersonLoading || isRemovePersonLoading || isUpdatePersonLoading;

  return (
    <>
      <span css={{ zIndex: 10_000, position: "relative" }}>
        <GlobalSpinner loading={isMutationLoading} />
      </span>
      <BreadcrumbsBox title="Employees">
        <Button
          icon="plus"
          onClick={() => {
            setIsEditMode(false);
            openSidebar();
          }}
        >
          Add new Contact Person
        </Button>
      </BreadcrumbsBox>
      <ErrorWrap error={error}>
        <Box spacing="spacing04">
          <SearchTable />
          <LoaderWrap loading={loading} Component={Loader}>
            <ReactTable
              striped
              columns={columns}
              data={filteredPeople.pageRows}
            />
            <Pagination count={filteredPeople.totalCount} />
          </LoaderWrap>
        </Box>
        <EmployeeForm
          initialValues={initialValues}
          isEditMode={isEditMode}
          isOpen={isSidebarOpen}
          onClose={() => closeSidebar()}
          onSubmit={(formData) =>
            isEditMode
              ? handleUpdatePerson(formData)
              : handleAddPerson(formData)
          }
        />
      </ErrorWrap>
      <ModalConfirm
        isOpen={isModalOpen}
        confirm={() => handleRemovePerson(deleteData.id)}
        actionLabel="Delete"
        contentLabel="Delete Contact Person"
        onClose={() => closeModal()}
      >
        {generateActionConfirmText(deleteData.name)("delete")()}
      </ModalConfirm>
    </>
  );
}

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={6}
      css={{
        marginTop: theme.spacing.spacing06,
      }}
      rows={[
        {
          count: 1,
          height: 20,
          items: [
            {
              widthPercent: 4,
            },
            {
              widthPercent: 32,
            },
            {
              widthPercent: 54,
            },
            {
              widthPercent: 10,
            },
          ],
        },
        {
          count: 6,
          height: 60,
          items: [
            {
              widthPercent: 4,
            },
            {
              widthPercent: 32,
            },
            {
              widthPercent: 54,
            },
            {
              widthPercent: 10,
            },
          ],
        },
      ]}
    />
  );
}
